/**
 * impersonationStore — Zustand store for admin view-only impersonation state.
 *
 * Holds { targetSub, targetEmail } as ephemeral UI state. In-memory only — this
 * persists across route navigation (mirrors calendarStore's month state) but NOT
 * across a full page reload. This is intentional: a reload is treated as a clean
 * exit from impersonation, so an admin can never get "stuck" viewing as someone
 * else across sessions, and there is no risk of a stale impersonation target
 * surviving a browser restart.
 *
 * Consumed via useImpersonationStore.getState() (non-hook) from apiRequest (lib/api.ts)
 * to attach the X-Impersonate-User header, and via the hook form in UI components
 * (AdminUserPicker, view-only gating) to react to changes.
 */
import { create } from 'zustand';
import { Hub } from 'aws-amplify/utils';

interface ImpersonationState {
  /** The impersonated user's Cognito sub, or null when not impersonating. */
  targetSub: string | null;
  /** The impersonated user's email, for display in the banner. */
  targetEmail: string | null;
  /** Start impersonating the given user. */
  setTarget: (sub: string, email: string) => void;
  /** Stop impersonating — reverts to the admin's own data. */
  clear: () => void;
}

export const useImpersonationStore = create<ImpersonationState>((set) => ({
  targetSub: null,
  targetEmail: null,

  setTarget: (sub, email) => set({ targetSub: sub, targetEmail: email }),

  clear: () => set({ targetSub: null, targetEmail: null }),
}));

// Auth-change invariant: impersonation must never survive an account switch. The store is
// in-memory (reload already clears it), but SPA sign-out → sign-in never reloads the page,
// so an admin's leftover View-as target used to leak into the NEXT user's session — a
// non-admin then had no picker with which to clear it, hiding all write UI (FAB/Add entry).
// Backend was never at risk (resolveAuth ignores the header for non-admins); this is purely
// the frontend view-only gating. Registered at module scope so it's active for the entire
// app lifetime — the store module loads with lib/api.ts in the entry chunk.
Hub.listen('auth', ({ payload }) => {
  if (payload.event === 'signedIn' || payload.event === 'signedOut') {
    useImpersonationStore.getState().clear();
  }
});
